import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { ITEM_TYPES } from "constants/productAndServices";
import { PRODUCTS_AND_SERVICES } from "constants/subscriptionPermissionFeatures";
import { REVENUE_DATA_SOURCE } from "constants/revenueDataSource";
import { getIn, useFormikContext } from "formik";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { useMemo, useState } from "react";
import { OnChangeValue } from "react-select";
import { useGetInfiniteGroupByProductsServicesInfiniteQuery } from "store/apis/productAndServices";
import { Invoice, InvoiceItem } from "types/Models/invoice";
import { ProductAndServicesDetail } from "types/Models/productAndServices";
import { debounce } from "utils/debouncing";
import { stopPropagation } from "utils/stopPropagation";
import ToolTip from "components/design/toolTip";
import Stripe from "static/images/Stripe.svg";

export const InvoiceItemSelector = ({
  createNewProduct,
  loading,
  invoiceItem,
  addNewProduct,
  onSelect,
  showTextField = false,
  size = "small",
}: {
  createNewProduct: (inputValue: string) => void;
  loading?: boolean;
  invoiceItem: InvoiceItem;
  addNewProduct: (inputValue?: string) => void;
  onSelect?: (item: ProductAndServicesDetail) => void;
  showTextField?: boolean;
  size?: "small" | "regular";
}) => {
  const { values, setFieldValue } = useFormikContext<Partial<Invoice>>();
  const group = useCurrentGroup();
  const groupId = group.uuid;
  const entityId = useCurrentEntityId();
  const [searchValue, setSearchValue] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const permission = useHasSubscriptionPermission({
    feature: PRODUCTS_AND_SERVICES,
  });

  const {
    data,
    isFetching: isProductsFetching,
    fetchNextPage,
    hasNextPage,
  } = useGetInfiniteGroupByProductsServicesInfiniteQuery(
    {
      groupId: groupId!,
      entityId,
      search_term: searchTerm,
    },
    {
      skip: !groupId || !entityId || permission?.blocked,
    }
  );

  const { products, services } = useMemo(
    () => ({
      products: data?.pages.flatMap((page) => page.products) || [],
      services: data?.pages.flatMap((page) => page.services) || [],
    }),
    [data]
  );

  const onSearchTermChange = useMemo(
    () => debounce((value: string) => setSearchTerm(value)),
    []
  );

  const onInputChange = (value: string) => {
    setSearchValue(value);
    onSearchTermChange(value);
  };

  const loadNextProducts = () => {
    if (hasNextPage && !isProductsFetching) {
      fetchNextPage();
    }
  };

  const buildOptionLabel = (name: string, source: string) => {
    const isStripe = source === REVENUE_DATA_SOURCE.STRIPE;

    if (!isStripe) {
      return name;
    }

    return (
      <div className="t-flex t-items-center t-justify-between t-w-full">
        <ToolTip
          text={<div className="t-text-left">{name}</div>}
          side="top"
          sideOffset={8}
        >
          <span className="t-truncate t-w-60">{name}</span>
        </ToolTip>

        <ToolTip
          text={
            <div className="t-text-left">
              This product is synced from Stripe and cannot be edited.
            </div>
          }
          side="right"
          sideOffset={8}
          propClass="t-max-w-60"
        >
          <img src={Stripe} alt="Stripe" className="t-h-5 t-w-5 t-shrink-0" />
        </ToolTip>
      </div>
    );
  };

  const productoptions = products.map(({ name, uuid, source }) => ({
    label: buildOptionLabel(name, source),
    value: uuid,
  }));

  const serviceoptions = services.map(({ name, uuid, source }) => ({
    label: buildOptionLabel(name, source),
    value: uuid,
  }));

  const selectedOption = invoiceItem.product_data_id
    ? [...productoptions, ...serviceoptions].find(
        ({ value }) => value === invoiceItem.product_data_id
      ) || {
        label: invoiceItem.description,
        value: invoiceItem.product_data_id,
      }
    : undefined;

  const options = [
    { label: "Products", options: productoptions },
    {
      label: "Services",
      options: serviceoptions,
    },
  ];

  const usingCustomInput =
    getIn(values, "description") && getIn(values, "product_data_id") === null;

  if (usingCustomInput || permission?.blocked || showTextField) {
    return (
      <TextInput
        aria-label="Item"
        customSize={size}
        name="description"
        required
        placeholder="Enter item name"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            onSelect?.({
              uuid: "",
              name: getIn(values, "description"),
              group_id: groupId || "",
              logo_url: "",
              source: "MANUAL",
              type: ITEM_TYPES.PRODUCT,
              product_category_name: "",
              product_category_id: "",
              transaction_category_id: "",
              transaction_category_name: "",
            });
          }
        }}
      />
    );
  }

  const handleSelect = (value: OnChangeValue<OptionData, boolean>) => {
    if (value instanceof Array) {
      return;
    }
    const currentItem = [...products, ...services].find(
      ({ uuid }) => uuid === value?.value
    );

    setFieldValue(`description`, currentItem?.name || "");

    if (onSelect && currentItem) {
      onSelect(currentItem);
    }
  };

  const onCustomInput = () => {
    setFieldValue("product_data_id", null);
    setFieldValue("description", searchValue);

    if (onSelect) {
      onSelect({
        uuid: "",
        name: searchValue,
        group_id: groupId || "",
        logo_url: null,
        source: "MANUAL",
        type: ITEM_TYPES.PRODUCT,
        product_category_name: "",
        product_category_id: "",
        transaction_category_id: "",
        transaction_category_name: "",
      });
    }
    setSearchValue("");
  };

  return (
    <Combobox
      onKeyDown={stopPropagation}
      aria-label="Item"
      withForm
      name="product_data_id"
      required
      size={size}
      onCreateOption={(inputValue) => {
        onCustomInput();
      }}
      onChange={handleSelect}
      isLoading={loading || isProductsFetching}
      isDisabled={loading}
      options={options}
      isClearable={false}
      creatable
      value={selectedOption}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      onInputChange={onInputChange}
      onMenuScrollToBottom={loadNextProducts}
      filterOption={null}
      formatCreateLabel={(inputValue) => `Add "${inputValue}" as custom input`}
      actions={
        <div className="t-flex t-flex-col t-items-start t-gap-2">
          {!searchValue && (
            <Button
              size="small"
              customType="link"
              type="button"
              onClick={() => addNewProduct()}
            >
              Add product
            </Button>
          )}
          {searchValue && (
            <Button
              size="small"
              customType="link"
              type="button"
              onClick={() => createNewProduct(searchValue)}
            >
              Add "{searchValue}" to My Products
            </Button>
          )}
        </div>
      }
    />
  );
};
